import { ReactNode, useState } from "react";
import React from "react";
import { Check, Copy, X } from "lucide-react";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { javascript, javascriptLanguage } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { LanguageSupport, syntaxTree } from "@codemirror/language";
import { linter } from "@codemirror/lint";
import type { Diagnostic } from "@codemirror/lint";
import { EditorView } from "@codemirror/view";
import { IgBorderColors } from "@intelligems/ig-design-tokens/constants";
import Stack from "@mui/material/Stack";
import CodeMirror, { ReactCodeMirrorProps } from "@uiw/react-codemirror";
import { IgIconButton } from "./IgIconButton";
import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

export interface CodeSyntaxValidation {
  isValid: boolean;
  /**
   * The full first-error message. For TypeScript this is the flattened
   * diagnostic chain (newline-separated); the first line is the headline and
   * the remainder is the elaboration. `null` when valid.
   */
  errorMessage: string | null;
  errorLine: number | null;
  errorColumn: number | null;
  /** Number of distinct error-severity diagnostics. `0` when valid. */
  errorCount: number;
}

export type JavaScriptSyntaxValidation = CodeSyntaxValidation;
export type TypeScriptSyntaxValidation = CodeSyntaxValidation;

export interface IgCodeEditorProps extends ReactCodeMirrorProps {
  language: "html" | "css" | "js" | "ts" | "json" | "md" | "text";
  /**
   * Shows the copy control. A string is used as its accessible label, so a
   * page with several editors can say what each one copies ("Copy input
   * payload") instead of repeating a bare "Copy".
   */
  copy?: boolean | string;
  fontSize?: string;
  helperText?: string;
  outline?: string;
  title?: ReactNode;
  lint?: boolean;
  lineWrapping?: boolean;
}

const getLineColumn = (source: string, offset: number) => {
  let line = 1;
  let column = 1;

  for (let index = 0; index < offset && index < source.length; index += 1) {
    if (source[index] === "\n") {
      line += 1;
      column = 1;
    } else {
      column += 1;
    }
  }

  return { line, column };
};

const validateCodeSyntax = (
  source: string,
  options?: { typescript?: boolean }
): CodeSyntaxValidation => {
  const parser = javascriptLanguage.configure({
    dialect: options?.typescript ? "ts" : undefined,
  }).parser;
  const cursor = parser.parse(source).cursor();
  let errorOffset: number | null = null;

  do {
    if (cursor.type.isError) {
      errorOffset = cursor.from;
      break;
    }
  } while (cursor.next());

  if (errorOffset === null) {
    return {
      isValid: true,
      errorMessage: null,
      errorLine: null,
      errorColumn: null,
      errorCount: 0,
    };
  }

  const { line, column } = getLineColumn(source, errorOffset);

  return {
    isValid: false,
    errorMessage: `Syntax error on line ${line}, column ${column}`,
    errorLine: line,
    errorColumn: column,
    errorCount: 1,
  };
};

export const validateJavaScriptSyntax = (
  source: string
): JavaScriptSyntaxValidation => validateCodeSyntax(source);

export const validateTypeScriptSyntax = (
  source: string
): TypeScriptSyntaxValidation =>
  validateCodeSyntax(source, { typescript: true });

/**
 * Outcome of a clipboard copy attempt. Unlike `navigator.clipboard.writeText`,
 * this never rejects — the rejection is captured so callers can surface
 * success/failure feedback to the user instead of silently swallowing the
 * error with `void`.
 */
export type CopyToClipboardResult =
  | { ok: true }
  | { ok: false; error: unknown };

/**
 * Writes `text` to the clipboard, normalizing every failure mode (missing
 * Clipboard API in non-secure/unsupported contexts, denied permission, the
 * document not being focused, etc.) into a resolved `{ ok: false }` result.
 */
export const writeTextToClipboard = async (
  text: string
): Promise<CopyToClipboardResult> => {
  try {
    if (!navigator.clipboard?.writeText) {
      return {
        ok: false,
        error: new Error("Clipboard API is not available"),
      };
    }
    await navigator.clipboard.writeText(text);
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
};

const javascriptSyntaxLinter = linter((view) => {
  const diagnostics: Diagnostic[] = [];
  const cursor = syntaxTree(view.state).cursor();

  do {
    if (!cursor.type.isError) {
      continue;
    }

    const line = view.state.doc.lineAt(cursor.from);
    const lineNumber = line.number;
    const column = cursor.from - line.from + 1;

    diagnostics.push({
      from: cursor.from,
      to: Math.max(cursor.to, cursor.from + 1),
      severity: "error",
      message: `Syntax error on line ${lineNumber}, column ${column}`,
    });
  } while (cursor.next());

  return diagnostics;
});

interface CopyButtonProps {
  /** Accessible label / default tooltip for the copy control. */
  label: string;
  /** The text written to the clipboard when the button is clicked. */
  value: string;
}

/**
 * Copy-to-clipboard control that surfaces success/failure feedback. The
 * clipboard write can fail (denied permission, non-secure context,
 * unsupported browser, unfocused document); rather than silently swallowing
 * the rejection, the result drives a transient "Copied!" / "Copy failed"
 * tooltip + icon state so the user always knows the real outcome.
 */
const CopyButton = ({ label, value }: CopyButtonProps) => {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">(
    "idle"
  );

  const handleCopy = async () => {
    const result = await writeTextToClipboard(value);
    setCopyStatus(result.ok ? "copied" : "error");
    setTimeout(() => setCopyStatus("idle"), 2000);
  };

  const tooltip =
    copyStatus === "copied"
      ? "Copied!"
      : copyStatus === "error"
        ? "Copy failed"
        : label;

  return (
    <IgTooltip title={tooltip}>
      <IgIconButton
        aria-label={label}
        title={tooltip}
        size="small"
        color={copyStatus === "error" ? "error" : "secondary"}
        onClick={() => void handleCopy()}
        sx={{ width: 28, height: 28, p: 0.5 }}
      >
        {copyStatus === "copied" ? (
          <Check size={14} />
        ) : copyStatus === "error" ? (
          <X size={14} />
        ) : (
          <Copy size={14} />
        )}
      </IgIconButton>
    </IgTooltip>
  );
};

export const IgCodeEditor = (props: IgCodeEditorProps) => {
  const {
    language,
    fontSize = "13px",
    outline,
    title,
    copy,
    value,
    helperText,
    lint = false,
    lineWrapping = true,
    extensions = [],
    style,
    "aria-label": ariaLabel,
    ...rest
  } = props;

  const wrapperStyle: React.CSSProperties = {};
  if (style?.flex !== undefined) {
    wrapperStyle.flex = style.flex;
  }
  if (style?.height !== undefined) {
    wrapperStyle.height = style.height;
  }
  if (style?.minHeight !== undefined) {
    wrapperStyle.minHeight = style.minHeight;
  }
  if (style?.maxHeight !== undefined) {
    wrapperStyle.maxHeight = style.maxHeight;
  }
  if (style?.overflow !== undefined) {
    wrapperStyle.overflow = style.overflow;
  }

  const hasWrapperStyle = Object.keys(wrapperStyle).length > 0;

  const theme = EditorView.theme({
    "&": {
      fontSize,
      borderRadius: "6px",
      overflow: "hidden",
      border: `1px solid ${IgBorderColors.core.default}`,
      outline: outline ?? "none",
    },
    "&.cm-focused": {
      borderColor: IgBorderColors.core.active,
      outline: outline ?? `2px solid ${IgBorderColors.accent.active}`,
      outlineOffset: "-2px",
    },
  });

  let langExt: LanguageSupport | undefined;
  switch (language) {
    case "text":
      langExt = undefined;
      break;
    case "html":
      langExt = html();
      break;
    case "css":
      langExt = css();
      break;
    case "js":
      langExt = javascript();
      break;
    case "ts":
      langExt = javascript({ typescript: true });
      break;
    case "json":
      langExt = json();
      break;
    case "md":
      langExt = markdown();
      break;
  }
  const lintExtensions =
    lint && (language === "js" || language === "ts")
      ? [javascriptSyntaxLinter]
      : [];

  return (
    <Stack spacing={0.5} style={hasWrapperStyle ? wrapperStyle : undefined}>
      {title || copy ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          {title ? (
            <IgTypography variant="caption" color="text.secondary">
              {title}
            </IgTypography>
          ) : (
            <span />
          )}
          {copy ? (
            <CopyButton
              label={typeof copy === "string" ? copy : "Copy"}
              value={value ?? ""}
            />
          ) : null}
        </Stack>
      ) : null}
      <CodeMirror
        {...rest}
        style={style}
        value={value}
        indentWithTab={true}
        extensions={[
          theme,
          ...(langExt ? [langExt] : []),
          ...(lineWrapping ? [EditorView.lineWrapping] : []),
          ...(ariaLabel
            ? [EditorView.contentAttributes.of({ "aria-label": ariaLabel })]
            : []),
          ...lintExtensions,
          ...extensions,
        ]}
      />
      {helperText && (
        <IgTypography variant="caption">{helperText}</IgTypography>
      )}
    </Stack>
  );
};
